// AGC: Tasks API Service
// AGC: Handles all API calls related to tasks
// AGC: All tasks are filtered by user account - each user only sees their own tasks
// AGC: useTasksQuery() provides TanStack Query + localStorage disk cache (stale-while-revalidate)

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getBackendUrl } from '../utils/backendUrl'
import { api } from './api'
import type { Task } from '../types'

// AGC: Backend URL for API calls
const BASE_URL = getBackendUrl()

// ============================================================
// AGC: LOCALSTORAGE DISK CACHE
// AGC: Instant display on mount; TanStack Query refetches when stale
// ============================================================

const TASKS_CACHE_KEY = 'droidfarm_tasks'

function readTasksDiskCache(): Task[] | undefined {
  try {
    const raw = localStorage.getItem(TASKS_CACHE_KEY)
    if (!raw) return undefined
    return JSON.parse(raw) as Task[]
  } catch {
    return undefined
  }
}

function writeTasksDiskCache(tasks: Task[]): void {
  try {
    localStorage.setItem(TASKS_CACHE_KEY, JSON.stringify(tasks))
  } catch { /* AGC: ignore quota errors */ }
}

// ============================================================
// AGC: TANSTACK QUERY HOOKS — cached task fetching + mutations
// ============================================================

/** AGC: Query hook — fetches tasks with disk cache for instant display */
export function useTasksQuery(enabled = true) {
  return useQuery({
    queryKey: ['tasks'],
    queryFn: async (): Promise<Task[]> => {
      const res = await api.get<{ tasks: Task[] }>('/api/v1/tasks')
      const data = res.tasks ?? []
      writeTasksDiskCache(data)
      return data
    },
    enabled,
    staleTime: 5 * 60 * 1000, // AGC: 5 minutes — background refetch only when stale
    placeholderData: readTasksDiskCache,
  })
}

/** AGC: Mutation hook — create task + update cache and disk */
export function useCreateTaskMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Task>) => api.post<{ task: Task }>('/api/v1/tasks', data),
    onSuccess: (res) => {
      if (res.task) {
        qc.setQueryData<Task[]>(['tasks'], (old) => {
          const updated = [res.task, ...(old || [])]
          writeTasksDiskCache(updated)
          return updated
        })
      }
    },
  })
}

/** AGC: Mutation hook — update task + update cache and disk */
export function useUpdateTaskMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data, strategy }: { id: string; data: Partial<Task>; strategy?: 'keep' | 'shift' }) =>
      api.put<{ task: Task }>(`/api/v1/tasks/${id}`, {
        ...data,
        ...(strategy && { updateFollowingTasksStrategy: strategy }),
      }),
    onSuccess: (res) => {
      if (res.task) {
        qc.setQueryData<Task[]>(['tasks'], (old) => {
          const updated = (old || []).map(t => t.id === res.task.id ? res.task : t)
          writeTasksDiskCache(updated)
          return updated
        })
      }
    },
  })
}

/** AGC: Mutation hook — delete task + update cache and disk */
export function useDeleteTaskMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/v1/tasks/${id}`),
    onSuccess: (_, id) => {
      qc.setQueryData<Task[]>(['tasks'], (old) => {
        const updated = (old || []).filter(t => t.id !== id)
        writeTasksDiskCache(updated)
        return updated
      })
    },
  })
}

/** AGC: Invalidate tasks query — forces background refetch */
export function useInvalidateTasks() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: ['tasks'] })
}

/**
 * AGC: Get all tasks from backend for the authenticated user
 * AGC: Requires authentication token - tasks are filtered by userId
 */
export const getTasks = async (authToken?: string | null): Promise<Task[]> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    // AGC: Add authentication token if provided
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    // AGC: cache: 'no-store' so list always reflects latest (e.g. after voice create or delete)
    const response = await fetch(`${BASE_URL}/api/v1/tasks`, {
      method: 'GET',
      headers,
      cache: 'no-store',
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.')
      }
      throw new Error(`Failed to fetch tasks: ${response.statusText}`)
    }

    const data = await response.json()
    const taskList = data.tasks || []
    return taskList
  } catch (error) {
    console.error('AGC: Error fetching tasks:', error)
    // AGC: Return empty array on error (graceful degradation) - new accounts start empty
    return []
  }
}

/**
 * AGC: Create a new task for the authenticated user
 * AGC: Requires authentication token - task is associated with userId
 */
export const createTask = async (
  taskData: Partial<Task>,
  authToken?: string | null
): Promise<Task> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    // AGC: Add authentication token if provided
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/tasks`, {
      method: 'POST',
      headers,
      body: JSON.stringify(taskData),
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.')
      }
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to create task: ${response.statusText}`)
    }

    const data = await response.json()
    return data.task
  } catch (error) {
    console.error('AGC: Error creating task:', error)
    throw error
  }
}

/**
 * AGC: Update a task for the authenticated user
 * @param taskId - The task ID to update
 * @param taskData - The task data to update
 * @param authToken - Authentication token
 * @param updateFollowingTasksStrategy - Optional strategy for updating following tasks when date changes
 *   - 'keep': Only change this task's date
 *   - 'shift': Shift all subsequent tasks in the same plot by the same number of days
 */
export const updateTask = async (
  taskId: string,
  taskData: Partial<Task>,
  authToken?: string | null,
  updateFollowingTasksStrategy?: 'keep' | 'shift'
): Promise<Task> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    // AGC: Include the strategy in the request body if provided
    const requestBody = {
      ...taskData,
      ...(updateFollowingTasksStrategy && { updateFollowingTasksStrategy }),
    }

    const response = await fetch(`${BASE_URL}/api/v1/tasks/${taskId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.')
      }
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to update task: ${response.statusText}`)
    }

    const data = await response.json()
    return data.task
  } catch (error) {
    console.error('AGC: Error updating task:', error)
    throw error
  }
}

/**
 * AGC: Get subsequent tasks for a given task in the same plot
 * AGC: Used to check if there are tasks that might need to be shifted
 * @param plotId - The plot ID to filter by
 * @param taskOrder - The order of the current task (tasks with higher order are subsequent)
 * @param authToken - Authentication token
 * @returns Array of subsequent tasks
 */
export const getSubsequentTasks = async (
  plotId: string,
  taskOrder: number,
  authToken?: string | null
): Promise<Task[]> => {
  try {
    const tasks = await getTasks(authToken)
    
    // AGC: Filter to tasks in the same plot with higher order
    const subsequentTasks = tasks.filter(task => 
      task.plotId === plotId && 
      task.order !== undefined && 
      task.order > taskOrder
    )
    
    // AGC: Sort by order
    return subsequentTasks.sort((a, b) => (a.order || 0) - (b.order || 0))
  } catch (error) {
    console.error('AGC: Error getting subsequent tasks:', error)
    return []
  }
}

/**
 * AGC: Delete a task for the authenticated user
 */
export const deleteTask = async (taskId: string, authToken?: string | null): Promise<void> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/tasks/${taskId}`, {
      method: 'DELETE',
      headers,
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.')
      }
      throw new Error(`Failed to delete task: ${response.statusText}`)
    }
  } catch (error) {
    console.error('AGC: Error deleting task:', error)
    throw error
  }
}

/**
 * AGC: Accept a task assignment — calls POST /api/v1/tasks/:id/accept.
 * AGC: Can only be called by the assigned user.
 * AGC: On success, the backend updates acceptanceStatus to 'accepted' and
 * AGC: sends a WhatsApp "Start" message to the assignee.
 * @param taskId - The task ID to accept
 * @param authToken - Authentication token of the assignee
 */
export const acceptTask = async (taskId: string, authToken?: string | null): Promise<void> => {
  try {
    const headers: HeadersInit = { 'Content-Type': 'application/json' }
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`

    const response = await fetch(`${BASE_URL}/api/v1/tasks/${taskId}/accept`, {
      method: 'POST',
      headers,
    })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication required. Please log in again.')
      if (response.status === 403) throw new Error('Only the assigned user can accept this task.')
      if (response.status === 409) throw new Error('Task already accepted.')
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to accept task: ${response.statusText}`)
    }
  } catch (error) {
    console.error('AGC: Error accepting task:', error)
    throw error
  }
}

/**
 * AGC: Start a task — calls POST /api/v1/tasks/:id/start.
 * AGC: Can only be called by the assigned user.
 * AGC: On success, the backend sets status to 'in-progress' and records actualStart.
 * @param taskId - The task ID to start
 * @param authToken - Authentication token of the assignee
 */
export const startTask = async (taskId: string, authToken?: string | null): Promise<void> => {
  try {
    const headers: HeadersInit = { 'Content-Type': 'application/json' }
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`

    const response = await fetch(`${BASE_URL}/api/v1/tasks/${taskId}/start`, {
      method: 'POST',
      headers,
    })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication required. Please log in again.')
      if (response.status === 403) throw new Error('Only the assigned user can start this task.')
      if (response.status === 409) throw new Error('Task already in progress.')
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to start task: ${response.statusText}`)
    }
  } catch (error) {
    console.error('AGC: Error starting task:', error)
    throw error
  }
}

/**
 * AGC: Finish a task — calls POST /api/v1/tasks/:id/finish.
 * AGC: Can only be called by the assigned user.
 * AGC: On success, backend sets status to 'completed' and progress to 100%.
 * @param taskId - The task ID to finish
 * @param authToken - Authentication token of the assignee
 */
export const finishTask = async (taskId: string, authToken?: string | null): Promise<void> => {
  try {
    const headers: HeadersInit = { 'Content-Type': 'application/json' }
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`

    const response = await fetch(`${BASE_URL}/api/v1/tasks/${taskId}/finish`, {
      method: 'POST',
      headers,
    })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication required. Please log in again.')
      if (response.status === 403) throw new Error('Only the assigned user can finish this task.')
      if (response.status === 409) throw new Error('Task already completed.')
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to finish task: ${response.statusText}`)
    }
  } catch (error) {
    console.error('AGC: Error finishing task:', error)
    throw error
  }
}

/**
 * AGC: Delete all tasks for the authenticated user (start from zero)
 * @returns Number of tasks deleted
 */
export const deleteAllTasks = async (authToken?: string | null): Promise<number> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }
    const response = await fetch(`${BASE_URL}/api/v1/tasks/all`, {
      method: 'DELETE',
      headers,
    })
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.')
      }
      const err = await response.json().catch(() => ({}))
      throw new Error(err.message || `Failed to delete all tasks: ${response.statusText}`)
    }
    const data = await response.json()
    const count = data.deletedCount ?? 0
    return count
  } catch (error) {
    console.error('AGC: Error deleting all tasks:', error)
    throw error
  }
}





